import React, { useState, useRef, useEffect, useMemo } from 'react';
import './SearchSelect.css';

const normalize = (s) => String(s || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase();

function SearchSelect({ options, value, onChange, placeholder = 'Rechercher...' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Recherche sans accents ni casse, mot par mot et dans n'importe quel ordre
  // (« lucas gabin », « spherik 135 »). Une option peut porter des mots-clés
  // en plus de son libellé (`search`), invisibles mais trouvables.
  const filtered = useMemo(() => {
    const words = normalize(query).split(/\s+/).filter(Boolean);
    if (words.length === 0) return options;
    return options.filter(o => {
      const haystack = normalize(`${o.label} ${o.search || ''}`);
      return words.every(w => haystack.includes(w));
    });
  }, [options, query]);

  const selectedLabel = value
    ? (options.find(o => o.value === value)?.label || '')
    : '';

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  return (
    <div className="search-select" ref={ref}>
      <div className={`search-select-trigger${open ? ' open' : ''}`} onClick={() => setOpen(!open)}>
        {value ? (
          <span className="search-select-value">
            {selectedLabel}
            <button type="button" className="search-select-clear" onClick={handleClear}>&times;</button>
          </span>
        ) : (
          <span className="search-select-placeholder">{placeholder}</span>
        )}
        <svg className="search-select-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {open && (
        <div className="search-select-dropdown">
          <div className="search-select-input-wrapper">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Taper pour filtrer..."
              className="search-select-input"
            />
          </div>
          <div className="search-select-options">
            <div
              className={`search-select-option ${!value ? 'selected' : ''}`}
              onClick={() => handleSelect('')}
            >
              {placeholder}
            </div>
            {filtered.map(opt => (
              <div
                key={opt.value}
                className={`search-select-option ${value === opt.value ? 'selected' : ''}`}
                onClick={() => handleSelect(opt.value)}
              >
                {opt.label}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="search-select-empty">Aucun résultat</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchSelect;
